import { useCallback } from "react";
import { useNavigate } from "react-router";

import { useStore } from "@/core/utils/hooks";
import { APP_ROUTES } from "@/core/constants";
import { Button } from "@/core/ui/atoms";
import {
  AdminMiniList,
  AdminMiniRow,
  AdminPanel,
  AdminStatCard,
  Banner,
  PageHeader,
} from "@/core/ui/molecules";

import { SuperAdminTenantsStore } from "@/app/tenants/data_sources/store";
import { PlanDefinitionsStore } from "@/app/plans/data_sources/store";
import { LibraryTemplatesStore } from "@/app/library/views/templates/data_sources/store";
import { LibraryTemplatePacksStore } from "@/app/library/views/template-packs/data_sources/store";

import { mockAuditEntries } from "@/app/library/data_sources/mock";

export function DashboardPage() {
  const navigate = useNavigate();
  const goToCreateTenant = useCallback(() => {
    navigate(APP_ROUTES.superAdmin.tenants.path);
  }, [navigate]);

  const { state: tenantsState } = useStore(SuperAdminTenantsStore.instance);
  const { state: plansState } = useStore(PlanDefinitionsStore.instance);
  const { state: templatesState } = useStore(LibraryTemplatesStore.instance);
  const { state: packsState } = useStore(LibraryTemplatePacksStore.instance);

  SuperAdminTenantsStore.instance.hooks.useTenantsStoreService({
    page: 1,
    page_size: 1,
  });
  PlanDefinitionsStore.instance.hooks.usePlanDefinitionsStoreService({
    page: 1,
    page_size: 1,
  });
  LibraryTemplatesStore.instance.hooks.useTemplateStoreService({
    page: 1,
    page_size: 1,
  });
  LibraryTemplatePacksStore.instance.hooks.useTemplatePackStoreService({
    page: 1,
    page_size: 1,
  });

  return (
    <>
      <PageHeader
        actions={
          <>
            <Button onClick={goToCreateTenant} variant="primary">
              Create Tenant
            </Button>
            <Button>Create Template Pack</Button>
          </>
        }
        breadcrumb={["Super Admin", "Dashboard"]}
        subtitle="Operational control panel for plans, tenants and the integrated Library."
        title="Dashboard"
      />
      <div className="mb-[18px] grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        <AdminStatCard
          label="Tenants"
          onClick={() => navigate(APP_ROUTES.superAdmin.tenants.path)}
          value={tenantsState?.count ?? 0}
        />
        <AdminStatCard
          label="Plans"
          onClick={() => navigate(APP_ROUTES.superAdmin.plans.path)}
          value={plansState?.count ?? 0}
        />
        <AdminStatCard
          label="Templates"
          onClick={() => navigate(APP_ROUTES.superAdmin.library.templates.path)}
          value={templatesState?.count ?? 0}
        />
        <AdminStatCard
          label="Template Packs"
          onClick={() => navigate(APP_ROUTES.superAdmin.library.packs.path)}
          value={packsState?.count ?? 0}
        />
      </div>
      <Banner tone="ok">
        <strong>Architecture locked:</strong> Library creates content. Super
        Admin controls availability. Plans allow packs. Tenant Setup installs
        copies atomically.
      </Banner>
      <div className="grid grid-cols-1 gap-[18px] xl:grid-cols-[minmax(0,1fr)_340px]">
        <AdminPanel className="p-[18px]">
          <h2 className="mb-3 text-[15px] font-extrabold text-ink">
            Recent Activity
          </h2>
          <AdminMiniList>
            {mockAuditEntries.length ? (
              mockAuditEntries.slice(0, 6).map((entry) => (
                <AdminMiniRow key={`${entry.when}-${entry.action}`}>
                  <span>
                    <strong>{entry.action}</strong>
                    <span className="block text-xs text-ink-muted">
                      {entry.when} · {entry.reason}
                    </span>
                  </span>
                  <span>{entry.target}</span>
                </AdminMiniRow>
              ))
            ) : (
              <AdminMiniRow>
                <span>
                  <strong>No activity yet</strong>
                  <span className="block text-xs text-ink-muted">
                    Use the demo buttons to generate events.
                  </span>
                </span>
                <span>-</span>
              </AdminMiniRow>
            )}
          </AdminMiniList>
        </AdminPanel>
        <AdminPanel className="p-[18px]">
          <h2 className="mb-3 text-[15px] font-extrabold text-ink">
            Demo Coverage
          </h2>
          <AdminMiniList>
            {["Create Tenant", "Plan Editor", "Library", "Create Pack"].map(
              (item) => (
                <AdminMiniRow key={item}>
                  <strong>{item}</strong>
                  <span className="rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[11px] font-extrabold text-emerald-700">
                    Functional
                  </span>
                </AdminMiniRow>
              ),
            )}
          </AdminMiniList>
        </AdminPanel>
      </div>
    </>
  );
}
